import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ShoppingBag, Phone } from 'lucide-react';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';

const LOGO_URL = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/485ee0f5-162d-499f-b44f-69bc80fbabf9/1785625914831-6ytm3p.png';

const LINKS = [
  { to: '/', label: 'Home' },
  { to: '/menu', label: 'Menu' },
  { to: '/about', label: 'Our Story' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Visit & Contact' },
];

export default function Navbar({ onCartOpen }: { onCartOpen: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const { itemCount } = useRestaurantCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-surface/95 backdrop-blur-md shadow-[0_2px_20px_rgba(42,19,16,0.08)]' : 'bg-surface/80 backdrop-blur-sm'
        } border-b border-line`}
        data-hs-id="navbar"
      >
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <img
              src={LOGO_URL}
              alt="Biryani2Go — Dum-Cooked Biryani Omaha & Council Bluffs Logo"
              className="h-12 md:h-14 w-auto object-contain shrink-0"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-ember ${
                  location.pathname === l.to ? 'text-ember' : 'text-ink-2'
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href="tel:+1-402-000-0000"
              className="md:hidden p-3 rounded-full hover:bg-surface2 transition-colors"
              aria-label="Call us"
              title="Click to call Biryani2Go"
            >
              <Phone size={22} className="text-maroon" />
            </a>
            <button
              onClick={onCartOpen}
              className="relative p-3 rounded-full hover:bg-surface2 transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={22} className="text-maroon" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-ember text-white text-[11px] font-bold flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </button>
            <Link to="/menu" className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-sm">
              Order Now
            </Link>
            <button
              className="md:hidden p-3 rounded-full hover:bg-surface2 transition-colors"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={24} className="text-maroon" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-maroon/60 z-[60] md:hidden"
              onClick={() => setDrawerOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-0 right-0 bottom-0 w-[82%] max-w-sm bg-surface z-[70] md:hidden flex flex-col shadow-2xl"
            >
              <div className="flex items-center justify-between h-20 px-5 border-b border-line">
                <span className="font-display font-bold text-xl text-maroon">Menu</span>
                <button onClick={() => setDrawerOpen(false)} className="p-2 rounded-full hover:bg-surface2" aria-label="Close menu">
                  <X size={24} className="text-maroon" />
                </button>
              </div>
              <nav className="flex flex-col p-5 gap-1">
                {LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className={`px-4 py-4 rounded-xl text-lg font-medium transition-colors ${
                      location.pathname === l.to ? 'bg-ember/10 text-ember' : 'text-ink-2 hover:bg-surface2'
                    }`}
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
              <div className="mt-auto p-5">
                <Link to="/menu" className="btn-primary w-full text-center">
                  Order Now
                </Link>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
